import { Controller } from '@hotwired/stimulus'
import computedValues from '../../helpers/computedValues'

import {
  connectFlowValue,
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  shouldDisableInteractionDuringTransition,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { generateElementSelectors } from '../../api/styles/selectors'
import updateValidationUI, {
  ValidationParent,
} from '../../helpers/controllerHelpers/customValidationUI'
import {
  OptionSelector,
  UserData,
  UserDataValue,
  OptionSelectorButton,
} from '../../api/models/types'
import { isEqual } from 'lodash'
import jitsu from '../../helpers/jitsu'
import renderTemplate, { extractTemplateKeys } from '../../api/helpers/templating.ts'
import { debounce } from '../../helpers/debounce'
import { getBaseBuilderVersion } from '../../api/helpers/utils.ts'

const CONTROLLER = 'components--option-selector'
export default class extends Controller {
  static classes = ['selected']
  static targets = ['option', 'select', 'currentOption', 'selectValueContainer']
  static values = {
    maxSelections: Number,
    multiple: Boolean,
    allowDeselect: Boolean,
    searchable: Boolean,
    dropdown: Boolean,
    advancedDropdown: Boolean,
    defaultVal: String,
  }

  disconnectUserData?: () => void
  _disconnectPageId?: () => void
  _disconnectDropdownPageId?: () => void
  removeListenerDataListCloseOnAnyClick?: () => void
  pageId: string
  component: OptionSelector
  flowId: string
  declare dropdownValue: string
  declare advancedDropdownValue: string
  cleanupElSelect?: () => void
  page: Element
  prevPage: Element
  declare hasOptionTarget: boolean
  declare optionTargets: HTMLElement[]
  declare defaultValValue?: string
  declare hasCurrentOptionTarget: boolean
  declare currentOptionTarget: HTMLElement
  declare hasSelectTarget: boolean
  declare selectTarget: Element
  declare hasSelectValueContainer: boolean
  declare selectValueContainer: HTMLElement
  declare multipleValue: boolean
  declare allowDeselectValue: boolean
  declare maxSelectionsValue: number
  declare searchableValue: boolean
  declare userDataValue: UserDataValue
  declare selectedClass: string
  flowEl: Element | null
  declare builderVersion: number

  declare templatedOptionsKeys: Set<string>

  datalistObserver: MutationObserver
  headerObserver: ResizeObserver
  wrapperObserver: ResizeObserver

  connect() {
    const { component, pageId, flowEl, flowId } = getComponentContext(this)
    this.pageId = pageId
    this.component = component as OptionSelector
    this.flowEl = flowEl
    this.flowId = (flowId as string) || ''

    this.builderVersion = getBaseBuilderVersion(getFlow(this, true)?.builder_version)

    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this._disconnectPageId = connectFlowValue(this, {
      attributeName: 'data-flow-page-id-value',
      outputValue: 'pageId',
    })

    if (this.dropdownValue) {
      this._disconnectDropdownPageId = connectFlowValue(
        this,
        { attributeName: 'data-flow-page-id-value' },
        this.pageIdValueChanged
      )

      this.updateSearchInputDisplayValue({
        userData: this.userDataValue as unknown as { [x: string]: string },
      })
    }

    if (this.advancedDropdownValue) {
      const wrapper = this.element.querySelector('.ElementType-OptionDropdown')
      const header = this.element.querySelector('.ElementType-DropdownMain')

      if (!this.element.getAttribute('data-controller')?.split(' ').includes('repeatable')) {
        //* If the dropdown is a repeatable, then I don't want to add the event listener to close the dropdown on any click
        //* But if the dropdown is a repeatable-button, then I want to add the event listener to close the dropdown on any click
        this.removeListenerDataListCloseOnAnyClick = this.addListenerDataListCloseOnAnyClick()
      }
      if (this.searchableValue) {
        const searchInput = this.element.querySelector('input')
        const valueText = searchInput?.getAttribute('value-text-showing')
        if (valueText && searchInput) {
          searchInput.value = valueText
        }
      }
      if (!header || !wrapper) return

      //* In builder v4, we won't be adding this resizers anymore
      if (this.builderVersion <= 3) {
        //* This observers work on the datalist element exclusively, to make them change margin and width of the datalist
        this.headerObserver = new ResizeObserver(() => this.updateDatalistTopMargin())
        this.wrapperObserver = new ResizeObserver(() => this.updateDatalistWidth())
        this.addResizeObservers(header, wrapper)
      }
    }

    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (typeof this.cleanupElSelect === 'function') this.cleanupElSelect()
    if (typeof this._disconnectPageId === 'function') this._disconnectPageId()
    if (typeof this._disconnectDropdownPageId === 'function') this._disconnectDropdownPageId()
    if (typeof this.removeListenerDataListCloseOnAnyClick === 'function')
      this.removeListenerDataListCloseOnAnyClick()
    if (this.datalistObserver) this.datalistObserver.disconnect()
    if (this.headerObserver) this.headerObserver.disconnect()
    if (this.wrapperObserver) this.wrapperObserver.disconnect()
  }

  pageIdValueChanged(pageId: string | number, oldPageId: string | number) {
    const pages = getFlow(this, true).pages || []
    const pagesById = pages.reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {})
    this.page = pagesById[pageId]
    this.prevPage = pagesById[oldPageId]
  }

  userDataValueChanged(newData: { [x: string]: string }, oldData: { [x: string]: string }) {
    const key = this.element.getAttribute('data-component-key-value')

    if (this.hasOptionTarget) {
      const targetsByValue = this.optionTargets.reduce((acc, el) => {
        const valueKey = el.getAttribute(`data-${CONTROLLER}-value-param`)
        acc[String(valueKey)] = el
        return acc
      }, {})

      //* Removing selected state if userData is reset from another page
      const lastValue = this.element.getAttribute('data-component-last-value')
      const oldValue =
        lastValue !== null && lastValue !== 'undefined' && lastValue !== ''
          ? JSON.parse(lastValue)
          : key && oldData?.[key]

      if (oldValue) {
        if (Array.isArray(oldValue)) {
          this.unSelectOptionAttributes(oldValue.map((val) => targetsByValue[val]).filter((t) => t))
        } else if (targetsByValue[oldValue]) {
          this.unSelectOptionAttributes([targetsByValue[oldValue]])
        }
      }

      let newValue = key && newData?.[key]
      if (newValue === undefined && this.defaultValValue) {
        const defaultValue = computedValues(this.defaultValValue, newData)
        /* If conditional only here to prevent potential infinite loops */
        if (defaultValue !== undefined) {
          this.dispatch('update', {
            detail: {
              key: this.element.getAttribute('data-component-key-value'),
              value: defaultValue,
              immediate: true,
              callback: () => this.triggerOnChangeActions(defaultValue),
            },
          })
          /* Will trigger handler again and skip this block */
          return
        }
      }
      const message = this.getValidationMessage(newValue)
      const target =
        this.hasCurrentOptionTarget && this.currentOptionTarget.getAttribute('type') === 'input'
          ? this.currentOptionTarget
          : this.hasSelectTarget
          ? this.selectTarget
          : null
      if (target) {
        this.element.setAttribute('show-validity-message', message ? 'true' : 'false')
        updateValidationUI(
          this,
          message,
          this.element as ValidationParent,
          target as HTMLObjectElement
        )
      }

      if (newValue) {
        if (Array.isArray(newValue)) {
          this.selectOptionAttributes(newValue.map((val) => targetsByValue[val]).filter((t) => t))
          if (this.dropdownValue) this.updateTagsList(newValue, targetsByValue)
        } else if (targetsByValue[newValue]) {
          this.selectOptionAttributes([targetsByValue[newValue]])
        }
      }
      //* Storing last value to remove selected state if userData is reset from another page
      this.element.setAttribute('data-component-last-value', JSON.stringify(newValue))
    }

    if (this.searchableValue) {
      this.updateSearchInputDisplayValue({ userData: newData })
    } else {
      this.updateSpanDisplayValue({ userData: newData })
    }
  }

  updateDatalistTopMargin() {
    //* When the header element of the dropdown is resized, this is called to update the datalist top margin
    const header = this.element.querySelector('.ElementType-DropdownMain') as HTMLElement
    const datalist = this.element.querySelector('datalist') as HTMLElement
    if (!header || !datalist) return
    datalist.style.top = header.offsetTop + header.offsetHeight - 1 + 'px'
  }

  updateDatalistWidth() {
    //* When the wrapper element of the dropdown is resized, this is called to update the datalist width
    if (!this.hasSelectTarget) return
    const options = this.selectTarget.querySelectorAll('option')
    const wrapper = this.element.querySelector('.ElementType-DropdownMain') as HTMLElement
    const datalist = this.element.querySelector('datalist') as HTMLElement

    const width = Math.max(
      ...Array.from(options).map((e: any) => {
        return wrapper.offsetWidth
      })
    )
    datalist.style.width = width + 'px'
    datalist.style.maxWidth = 'unset'
  }

  updateDataListOptionLabel(userDataValue?: { [x: string]: string } | UserDataValue) {
    userDataValue = userDataValue || this.userDataValue

    const datalist = this.element.querySelector('datalist')
    if (!datalist) return

    const options = datalist.querySelectorAll('.option')
    options.forEach((option) => {
      const hasTemplate = option.getAttribute('data-controller')?.includes('template')
      if (!hasTemplate) return

      const text = decodeURIComponent(option.getAttribute('data-template-text-value') || '')
      const templatedText = renderTemplate(text, userDataValue as { [x: string]: string })
      option.setAttribute('label', templatedText || '')
    })
  }

  updateTagsList(tags: any[], targetsByValue: { [x: string]: { innerText: string } }) {
    if (!Array.isArray(tags)) return

    //* We couldn't find a case where this would be true
    if (this.hasSelectValueContainer) {
      const tagClass = 'option-selector-tag'
      const currentTags = this.selectValueContainer.querySelectorAll(`.option-selector-tag`)
      const tagvalues = new Set(tags.map((t) => `${t}`))
      const seenTags = new Set()

      currentTags.forEach((el) => {
        const value = el.getAttribute('data-value')
        /* If the tag is not in the tags array, remove it; else add it to a set of seen tags for use below */
        if (value && !tagvalues.has(value)) el.remove()
        else seenTags.add(value)
      })
      tagvalues.forEach((val) => {
        /* Tag already exists */
        if (seenTags.has(val)) return

        const tag = document.createElement('div')
        const tagText = document.createElement('span')
        const tagClose = document.createElement('span')
        tagText.innerText = targetsByValue[val] ? targetsByValue[val].innerText : val
        tagClose.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"><path d="M6.895455 5l2.842897-2.842898c.348864-.348863.348864-.914488 0-1.263636L9.106534.261648c-.348864-.348864-.914489-.348864-1.263636 0L5 3.104545 2.157102.261648c-.348863-.348864-.914488-.348864-1.263636 0L.261648.893466c-.348864.348864-.348864.914489 0 1.263636L3.104545 5 .261648 7.842898c-.348864.348863-.348864.914488 0 1.263636l.631818.631818c.348864.348864.914773.348864 1.263636 0L5 6.895455l2.842898 2.842897c.348863.348864.914772.348864 1.263636 0l.631818-.631818c.348864-.348864.348864-.914489 0-1.263636L6.895455 5z"></path></svg>`
        tag.setAttribute('data-element', 'true')
        setTagClasses(tag, 'DropdownMainContentItem', this.component, this.page)
        tagText.setAttribute('data-element', 'true')
        setTagClasses(tagText, 'DropdownMainContentItemText', this.component, this.page)
        tagClose.setAttribute('data-element', 'true')
        setTagClasses(tagClose, 'DropdownMainContentItemClose', this.component, this.page)
        setTagClasses(
          tagClose.firstChild,
          'DropdownMainContentItemCloseIcon',
          this.component,
          this.page
        )
        tagClose.children[0]?.setAttribute('data-element', 'true')
        /* NYI - tag styling for selector .OptionSelector div.select div.header span .option-selector-tag in generator.ts */
        tag.classList.add(tagClass)
        tag.setAttribute('data-value', val)
        tag.appendChild(tagText)
        tag.appendChild(tagClose)
        tagClose.setAttribute('data-action', `click->${CONTROLLER}:selectOption`)
        tagClose.setAttribute(
          `data-${CONTROLLER}-key-param`,
          `${this.element.getAttribute('data-component-key-value')}`
        )
        tagClose.setAttribute(`data-${CONTROLLER}-value-param`, val)

        /* fallback, the if condition should always be true but have to prevent errors or stimulus crashes */
        if (this.hasCurrentOptionTarget) tag.insertBefore(this.currentOptionTarget, tag)
        else this.selectValueContainer.appendChild(tag)
      })
    }
    if (this.hasCurrentOptionTarget) {
      this.searchableValue
        ? this.currentOptionTarget.setAttribute('value', '')
        : this.currentOptionTarget.setAttribute('innerText', '')
    }
  }

  selectOptionAttributes(
    optionEls: {
      innerText: string
      classList: { add: (arg0: string) => void }
      setAttribute: (arg0: string, arg1: string) => void
      querySelectorAll: (arg0: string) => any[]
    }[]
  ) {
    if (this.hasCurrentOptionTarget) {
      const textVal =
        optionEls.map((optionEl) => optionEl.innerText).join(', ') ||
        this.component.placeholder ||
        'Select an option...'
      this.searchableValue
        ? ((this.currentOptionTarget as HTMLInputElement).value = `${textVal}`)
        : (this.currentOptionTarget.innerText = textVal)
    }

    optionEls.forEach((optionEl) => {
      optionEl.classList.add(this.selectedClass)
      optionEl.setAttribute('selected', 'selected')
      optionEl.querySelectorAll('.Checkbox').forEach((el) => {
        el.classList.add('selected')
        el.setAttribute('data-icon-visible-value', 'true')
      })
    })
  }

  unSelectOptionAttributes(
    optionEls: {
      classList: { remove: (arg0: string) => void }
      removeAttribute: (arg0: string) => void
      querySelectorAll: (arg0: string) => any[]
    }[]
  ) {
    const self = this
    optionEls.forEach((optionEl) => {
      optionEl.classList.remove(self.selectedClass)
      optionEl.removeAttribute('selected')
      optionEl.querySelectorAll('.Checkbox').forEach((el) => {
        el.classList.remove('selected')
        //* Removing checkbox icon button is unselected by resetting userData from another page
        el.setAttribute('data-icon-visible-value', 'false')
      })
    })
  }

  evaluateRunningActions(event) {
    if ((event as KeyboardEvent).key === ' ') event.preventDefault()
    if ((event as KeyboardEvent).key === 'Enter' || (event as KeyboardEvent).key === ' ') {
      this.selectOption(event)
    }
  }

  advancedDropdownAccessibility(event) {
    //* Preventing scrolling
    if (
      ((event as KeyboardEvent).key === ' ' &&
        (event.target as HTMLElement).tagName.toLowerCase() !== 'input') ||
      (event as KeyboardEvent).key === 'ArrowDown' ||
      (event as KeyboardEvent).key === 'ArrowUp'
    )
      event.preventDefault()

    const dropdown = this.element.querySelector('.ElementType-OptionDropdown') as HTMLElement
    const textInput =
      (this.element.querySelector('.ElementType-DropdownMainContentInput') as HTMLElement) || null
    const accessibilityTarget = textInput || dropdown
    const currentTabIndex = (event.target as HTMLElement).tabIndex
    const isOpen = dropdown?.hasAttribute('data-open') ? true : false

    //* If the dropdown is not searchable then if the user presses enter or spacebar, run the selectDatalistOptionOnAdvancedDropdown method and then focus on dropdown. This way we don't lose the position for tabbing
    if (
      ((event as KeyboardEvent).key === 'Enter' || (event as KeyboardEvent).key === ' ') &&
      !textInput &&
      isOpen
    ) {
      this.selectDatalistOptionOnAdvancedDropdown(event)
      dropdown.focus()
      return
    }

    //* If the user presses ArrowDown then change focus to the next tabIndex in the dropdown
    if ((event as KeyboardEvent).key === 'ArrowDown' && isOpen) {
      let nextTabIndexToCheck = currentTabIndex + 1
      let nextElToCheck = this.element.querySelector(
        `[tabindex="${nextTabIndexToCheck}"]`
      ) as HTMLElement
      //* Look for the next visible element
      while (nextElToCheck && nextElToCheck.style.display === 'none') {
        nextTabIndexToCheck++
        nextElToCheck = this.element.querySelector(
          `[tabindex="${nextTabIndexToCheck}"]`
        ) as HTMLElement
      }
      //* Set focus to the next visible element
      if (nextElToCheck) {
        nextElToCheck.focus()
      }
      return
    }

    //* If the user presses ArrowUp then change focus to the previous tabIndex in the dropdown but don't go below 1
    if ((event as KeyboardEvent).key === 'ArrowUp' && isOpen) {
      let prevTabIndexToCheck = currentTabIndex - 1
      let prevElToCheck = this.element.querySelector(
        `[tabindex="${prevTabIndexToCheck}"]`
      ) as HTMLElement
      //* Look for the next visible element
      while (prevElToCheck && prevElToCheck.style.display === 'none') {
        prevTabIndexToCheck--
        prevElToCheck = this.element.querySelector(
          `[tabindex="${prevTabIndexToCheck}"]`
        ) as HTMLElement
      }
      //* Set focus to the next visible element
      if (prevElToCheck) {
        prevElToCheck.focus()
      }
      return
    }

    //* If the user presses Escape or tab then close the dropdown
    if (
      ((event as KeyboardEvent).key === 'Escape' || (event as KeyboardEvent).key === 'Tab') &&
      isOpen
    ) {
      accessibilityTarget.click()
      return
    }

    //* If the user presses space or arrow down then open the dropdown
    if (
      ((event as KeyboardEvent).key === ' ' || (event as KeyboardEvent).key === 'ArrowDown') &&
      !isOpen
    ) {
      accessibilityTarget.click()
      return
    }

    //* If the user presses any other key, textInput exists, and isOpen is not true then  click on accessibilityTarget
    if (
      textInput &&
      !isOpen &&
      (event as KeyboardEvent).key !== 'Tab' &&
      (event as KeyboardEvent).key !== 'Escape' &&
      (event as KeyboardEvent).key !== 'Enter'
    ) {
      accessibilityTarget.click()
    }
  }

  selectOption(event: {
    params: {
      value: string
      key: string | number
      singleSelect: string
      triggerEvent: string
      url: string
      newTab: string
    }
    target: { value: string }
  }) {
    if (shouldDisableInteractionDuringTransition(this)) return

    const eventParam = event.params.value || event.target?.value
    const paramValue: any = eventParam

    const buttons = this.multipleValue
      ? this.optionTargets.map((el) => ({
          singleSelect: !!el.getAttribute(`data-${CONTROLLER}-single-select-param`),
          key: el.getAttribute(`data-${CONTROLLER}-value-param`),
        }))
      : []

    const value = this.multipleValue
      ? toggle(
          Array.isArray(this.userDataValue[event.params.key])
            ? this.userDataValue[event.params.key]
            : [],
          paramValue,
          this.maxSelectionsValue,
          event.params.singleSelect,
          buttons,
          this.component.block_selection_over_max
        )
      : this.allowDeselectValue
      ? paramValue === this.userDataValue[event.params.key]
        ? null
        : paramValue
      : paramValue

    const triggerEvent = event.params.triggerEvent
    const goNext = this.dropdownValue
      ? false
      : (triggerEvent === undefined && !this.multipleValue) || triggerEvent === 'next-page'
    const old_value = this.userDataValue[event.params.key]
    /* Pass Go Next in to guarantee that conditions have processed with the immediate flag by the time next is called */
    this.dispatch('update', {
      detail: {
        key: event.params.key,
        value,
        immediate: true,
        goNext,
        pageId: this.pageId,
        callback: () => this.triggerOnChangeActions(value),
      },
    })

    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.pageId)
    const componentIndex = flow?.pages[pageIndex]?.components.findIndex(
      (component) => component.id === this.component.id
    )

    if (!this.multipleValue && triggerEvent === 'open-url') {
      const url = event.params.url
      window.open(url, event.params.newTab ? '_blank' : '_self')
    }

    this.dispatch('actions', {
      detail: {
        actions: (this.component.conversions || []).map((c) => ({ ...c, output: 'conversion' })),
        component: this.component,
      },
    })

    if (doNotStoreComponentDataInCloud(this.component)) return

    window.Savvy.addEventLogs?.([
      {
        flow_id: this.flowId,
        event_type: 'option:selected',
        timestamp: Date.now(),
        user_data_snapshot: this.userDataValue as unknown as UserData,
        data: {
          component_id: this.component.id,
          component_key: this.component.key,
          component_path: [
            ...(this.pageId ? ['pages', `${pageIndex}`] : []),
            'components',
            `${componentIndex}`,
          ],
          new_value: value,
          old_value,
          clicked_value: event.params.key,
        },
      },
    ])

    jitsu.track('option:selected', {
      flow_id: this.flowId,
      option_selector_id: this.component.id,
      option_selector_key: this.component.key,
      option_selector_value: Array.isArray(value) ? JSON.stringify(value) : value,
    })
  }

  getValidationMessage(value: string | null) {
    const component = this.component
    if (!component) return ''
    const isValid = component.isRequired ? Boolean(value) : true
    if (isValid) return ''
    if (!value && component.empty_invalid_message) return component.empty_invalid_message
    if (value && component.invalid_message) return component.invalid_message
    switch (component.validation_formula) {
      default:
        return 'Please fill out this field.'
    }
  }

  triggerOnChangeActions(newValue: any) {
    if (!newValue) newValue = undefined
    //* I trigger onchange actions after waiting for the userData update to complete
    if (this.component?.outputs_onchange) {
      const actions = this.component.outputs_onchange
      this.dispatch('actions', {
        detail: {
          actions: actions,
          component: this.component,
        },
      })
    }
  }

  toggleAdvancedDropdown(event?: MouseEvent) {
    //* This function is called when the user clicks on the dropdown to open or close it
    event?.preventDefault()
    event?.stopPropagation()

    const searchInput = this.element.querySelector('input')
    const wrapper = this.element.querySelector('.ElementType-OptionDropdown')

    if (this.searchableValue) searchInput?.focus()
    const open = wrapper?.hasAttribute('data-open')
    document.querySelectorAll('.ElementType-OptionDropdown[data-open]').forEach((el) => {
      if (el !== wrapper) el.removeAttribute('data-open')
    })
    if (open) {
      wrapper?.removeAttribute('data-open')
      //* If the dropdown is searchable, then I need to close the dropdown and remove the display none from the other options elements
      if (this.searchableValue) this.closeOnSearchableDropdownDatalist()
    } else {
      wrapper?.setAttribute('data-open', '')
    }
  }

  keyPressOnAdvancedDropdown(event: KeyboardEvent) {
    event.preventDefault()

    if (event.key === 'Enter' || event.keyCode === 13) {
      this.toggleAdvancedDropdown()
    }
  }

  searchInputHandler(event: KeyboardEvent) {
    //* This function is called when the user types in the search input and filters the options inside the datalist
    const target = event.target as HTMLInputElement
    const datalist = this.element.querySelector('datalist')
    const searchString =
      typeof target.value === 'string' ? target.value.toLowerCase() : target.value

    if (!datalist) return
    Array.from(datalist.children).forEach((el) => {
      const element = el as HTMLElement
      const matchesString =
        element.getAttribute('value')?.toLowerCase().includes(searchString) ||
        element.getAttribute('label')?.toLowerCase().includes(searchString)
      element.style.display = matchesString ? '' : 'none'
    })
  }

  closeOnSearchableDropdownDatalist() {
    //* This function is called when the user clicks outside the datalist to close the dropdown
    const datalist = this.element.querySelector('datalist')
    const searchInput = this.element.querySelector('input')

    if (!searchInput) return
    searchInput.blur()
    this.updateSearchInputDisplayValue({
      userData: this.userDataValue as unknown as { [x: string]: string },
    })

    if (!datalist) return
    //* As soon as the user closes the dropdown, I need to remove the display none from the other options elements
    Array.from(datalist.children).forEach((el: any) => {
      el.style.display = ''
    })
  }

  selectDatalistOptionOnAdvancedDropdown(event: MouseEvent | KeyboardEvent) {
    if (shouldDisableInteractionDuringTransition(this)) return

    //* This function is called when the user clicks on an option inside the datalist
    const clickedOption = (event?.target as HTMLElement).closest('.option')
    const datalist = this.element.querySelector('datalist')
    const wrapper = this.element.querySelector('.ElementType-OptionDropdown')

    if (!datalist) return

    //* Remove the display none from the other options elements (filtered)
    Array.from(datalist.children).forEach((el) => {
      const element = el as HTMLElement
      element.style.display = ''
    })

    //* If option is disabled, return
    const disabled = clickedOption?.hasAttribute('data-disabled')
    if (disabled) return

    //* I update the display value
    if (this.searchableValue) {
      this.updateSearchInputDisplayValue({
        value: clickedOption?.getAttribute('value') || undefined,
      })
    } else {
      this.updateSpanDisplayValue({
        value: clickedOption?.getAttribute('value') || undefined,
      })
    }

    this.selectOption({
      params: {
        key: clickedOption?.getAttribute(`data-${CONTROLLER}-key-param`) || '',
        value: clickedOption?.getAttribute(`data-${CONTROLLER}-value-param`) || '',
        singleSelect: clickedOption?.getAttribute(`data-${CONTROLLER}-single-select-param`) || '',
        triggerEvent: clickedOption?.getAttribute(`data-${CONTROLLER}-trigger-event-param`) || '',
        url: clickedOption?.getAttribute(`data-${CONTROLLER}-url-param`) || '',
        newTab: clickedOption?.getAttribute(`data-${CONTROLLER}-new-tab-param`) || '',
      },
      target: { value: clickedOption?.getAttribute(`data-${CONTROLLER}-value-param`) || '' },
    })

    if (this.multipleValue) {
      if (event?.shiftKey) {
        clickedOption?.hasAttribute('data-checked')
          ? clickedOption?.removeAttribute('data-checked')
          : clickedOption?.setAttribute('data-checked', '')
      } else {
        const options = wrapper?.querySelectorAll('.option')
        options?.forEach((option) => option.removeAttribute('data-checked'))
        clickedOption?.setAttribute('data-checked', '')
      }
    }
  }

  updateSearchInputDisplayValue({
    userData,
    value,
  }: {
    userData?: { [x: string]: string | string[] }
    value?: string | string[]
  }) {
    const newValue = userData ? userData[this.component.key] : value
    const newValueIsArray = Array.isArray(newValue)

    //* If we have a value, then set the value of the input to the selected option label
    if ((newValueIsArray && newValue.length) || (!newValueIsArray && newValue)) {
      const optionElements = Array.from(this.element.querySelectorAll('.option'))
      if (!optionElements) return

      const newValueArray = newValueIsArray ? newValue : [newValue]
      const selectedOptions = newValueArray
        .map((val) => optionElements.find((option) => option.getAttribute('value') === val))
        .filter((o) => o)
      const selectedOptionsLabel = selectedOptions.map(
        (selectedOption) => selectedOption?.getAttribute('label') || ''
      )
      const selectedOptionsText = selectedOptionsLabel.join(', ') || ''

      const searchInput = this.element.querySelector('input')
      if (!searchInput) return
      searchInput?.setAttribute('value-text-showing', selectedOptionsText)
      searchInput.value = selectedOptionsText
    } else {
      //* If new value is undefined, then set the value of the input to ""
      const searchInput = this.element.querySelector('input')
      if (!searchInput) return
      searchInput?.setAttribute('value-text-showing', '')
      searchInput.value = ''
    }
  }

  updateSpanDisplayValue({
    userData,
    value,
  }: {
    userData?: { [x: string]: string | string[] }
    value?: string | string[]
  }) {
    const newValue = userData ? userData[this.component.key] : value
    const newValueIsArray = Array.isArray(newValue)
    const defaultSpanText = this.component.placeholder || this.component.defaultVal || ''

    //* If we have a value, then set the innerText of the span to the selected option label
    if ((newValueIsArray && newValue.length) || (!newValueIsArray && newValue)) {
      const optionElements = Array.from(this.element.querySelectorAll('.option'))
      if (!optionElements) return

      const newValueArray = newValueIsArray ? newValue : [newValue]

      const selectedOptions = newValueArray
        .map((val) => optionElements.find((option) => option.getAttribute('value') === val))
        .filter((o) => o)
      const selectedOptionsLabel = selectedOptions.map(
        (selectedOption) => selectedOption?.getAttribute('label') || ''
      )
      const selectedOptionsText = selectedOptionsLabel.join(', ') || ''

      const span = this.element.querySelector('span')
      if (!span) return
      span.innerText = selectedOptionsText || defaultSpanText
    } else {
      //* I need to check if userData is undefined or empty array
      const userDataComponentValue = this.userDataValue[this.component.key]
      if (
        (!Array.isArray(userDataComponentValue) && userDataComponentValue) ||
        (Array.isArray(userDataComponentValue) && userDataComponentValue.length)
      )
        return

      //* If newvalue is undefined, then set the innerText of the span to the default value
      const span = this.element.querySelector('span')
      if (!span) return
      span.innerText = defaultSpanText
    }
  }

  keyUpOnDatalistOptionOnAdvancedDropdown(event: KeyboardEvent) {
    //* This function is called when the user presses enter on an option inside the datalist
    if (event.key === 'Enter' || event.keyCode === 13) {
      this.selectDatalistOptionOnAdvancedDropdown({
        target: event.target as HTMLElement,
        shiftKey: event.shiftKey,
      } as unknown as MouseEvent)
    }
  }

  addListenerDataListCloseOnAnyClick() {
    //* This function adds an event listener to close the dropdown when the user clicks outside the datalist
    if (typeof this.removeListenerDataListCloseOnAnyClick === 'function')
      this.removeListenerDataListCloseOnAnyClick()

    const datalist = this.element.querySelector('datalist')
    if (datalist) {
      const wrapper = this.element.querySelector('.ElementType-OptionDropdown')
      const onAnyClick = () => {
        if (wrapper?.hasAttribute('data-open')) {
          wrapper?.removeAttribute('data-open')
          if (this.searchableValue) this.closeOnSearchableDropdownDatalist()
        }
      }
      document.addEventListener('click', onAnyClick)
      return () => document.removeEventListener('click', onAnyClick)
    }
  }

  addResizeObservers(header?, wrapper?) {
    //* This function adds resize observers to the header and wrapper elements of the dropdown
    if (this.headerObserver) this.headerObserver.disconnect()
    if (this.wrapperObserver) this.wrapperObserver.disconnect()

    wrapper = wrapper || this.element.querySelector('.ElementType-OptionDropdown')
    header = header || this.element.querySelector('.ElementType-DropdownMain')
    this.headerObserver.observe(header)
    this.wrapperObserver.observe(wrapper)
  }
}

function toggle(
  _arr: any[],
  val: string,
  maxSelections: number,
  singleselect: string,
  buttons: any[],
  block_selection_over_max?: boolean
) {
  if (!val) return _arr
  if (singleselect) return [val]
  const singleSelectKeys = new Set(buttons.filter((b) => b.singleSelect).map((b) => b.key))
  const arr = _arr.filter((v) => !singleSelectKeys.has(v))

  const index = arr.indexOf(val)

  if (index === -1) {
    //* If block_selection_over_max is true, then don't add the value to the array if the maxSelections is reached
    if (block_selection_over_max && arr.length >= maxSelections) return arr
    return maxSelections ? [...arr, val].slice(-1 * maxSelections) : [...arr, val]
  } else {
    const newArr = [...arr]
    newArr.splice(index, 1)
    return newArr
  }
}

/* NYI - page reactivity when this is a global component */
function setTagClasses(el, elName, component, page, prevPage?) {
  const setClass = (extraTags?, extras?) => {
    if (prevPage)
      el.classList.remove(
        ...generateElementSelectors({
          elementType: elName,
          component,
          page: prevPage,
          elementTags: extraTags,
          elementExtras: extras,
          builderVersion: this.builderVersion,
        })
      )
    el.classList.add(
      ...generateElementSelectors({
        elementType: elName,
        component,
        page,
        elementTags: extraTags,
        elementExtras: extras,
        builderVersion: this.builderVersion,
      })
    )
  }
  switch (elName) {
    default: {
      setClass()
      break
    }
  }
}
